/* ═══════════════════════════════════════════════════════════════
   ProjectBudgetService — budgets attached to a project.

   Three nesting levels in one table:
     - row with phaseId === undefined && billingRoleId === undefined
       → project-total budget
     - row with phaseId set, billingRoleId === undefined
       → phase-total budget
     - row with both set → finest grain (phase × billing-role)

   The UI ladders these progressively: a project starts with one
   project-total row.  Drilling in lets the user enter phase-level
   numbers (which roll up to validate against the project total).
   Drilling further breaks each phase into roles.
   ═══════════════════════════════════════════════════════════════ */

import type { ProjectBudget } from '../../shared/entities';
import * as cache from './cloudCache';

const TABLE = 'project_budgets';

export const ProjectBudgetService = {
  list: async (): Promise<ProjectBudget[]> => cache.list<ProjectBudget>(TABLE),

  /** All budget rows for a single project. */
  listForProject: async (projectId: string): Promise<ProjectBudget[]> => {
    const all = await ProjectBudgetService.list();
    return all.filter((b) => b.projectId === projectId);
  },

  /** The single project-total row (phase + role both null). Returns null
   *  when no project-total budget has been entered yet. */
  getProjectTotal: async (projectId: string): Promise<ProjectBudget | null> => {
    const all = await ProjectBudgetService.listForProject(projectId);
    return all.find((b) => !b.phaseId && !b.billingRoleId) || null;
  },

  save: async (budget: ProjectBudget): Promise<ProjectBudget> => cache.save(TABLE, budget),

  delete: async (id: string): Promise<void> => cache.remove(TABLE, id),

  /** Convenience: roll up rows to a project total when no explicit
   *  project-total row exists. Phase-level rows take priority over the
   *  finer phase × role rows (we don't double-count). */
  computeRollup: (rows: ProjectBudget[]): { hours: number; amount: number } => {
    const phaseRows = rows.filter((r) => r.phaseId && !r.billingRoleId);
    const roleRows = rows.filter((r) => r.phaseId && r.billingRoleId);
    const phasesWithTotals = new Set(phaseRows.map((r) => r.phaseId));
    const useRoleRows = roleRows.filter((r) => !phasesWithTotals.has(r.phaseId));
    const all = [...phaseRows, ...useRoleRows];
    return {
      hours: all.reduce((s, r) => s + (r.budgetHours || 0), 0),
      amount: all.reduce((s, r) => s + (r.budgetAmount || 0), 0),
    };
  },
};
